<template>
  <div>
    <!-- Hero Section -->
    <v-container fluid class="hero-section py-24 text-center text-white" :style="heroBgStyle">
      <v-row justify="center">
        <v-col cols="12" md="8">
          <img src="/logo.png" alt="Reeflendar Logo" class="hero-logo mb-4">
          <h1 class="display-2 font-weight-bold">Track. Visualize. Thrive.</h1>
          <p class="text-h5 mt-4">Log water parameters, visualize trends, and stay on top of your reef's health.</p>
          <div
            class="hero-btn-group"
            :class="{ 'animate-in': heroBtnAnimated }"
          >
            <v-btn
              variant="elevated"
              color="primary"
              :to="{ name: 'Aquarium Demo' }"
            >
              View Demo Aquarium
            </v-btn>
            <v-btn
              variant="elevated"
              color="white"
              href="https://youtu.be/UQa1Q2R_jpo"
              target="_blank"
            >
              Watch Intro Video
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Features Section -->
    <v-container class="py-12">
      <v-row>
        <v-col cols="12" class="text-center mb-10">
          <h2 class="text-h4 font-weight-bold">Why Reeflendar?</h2>
        </v-col>
      </v-row>
      <v-row>
        <FeatureCard
          v-for="(feature, index) in features"
          :key="index"
          :icon="feature.icon"
          :title="feature.title"
          :description="feature.description"
        />
      </v-row>
    </v-container>

    <!-- Testimonials Section --> <!--
    <v-container class="py-12 grey lighten-4">
      <v-row>
        <v-col cols="12" class="text-center mb-10">
          <h2 class="text-h4 font-weight-bold">What Aquarists Are Saying</h2>
        </v-col>
        <v-col cols="12" md="4">
          <v-card>
            <v-card-text>
              <p>"Reeflendar changed the way I track my reef tank. It's intuitive and powerful."</p>
              <p class="font-weight-bold mt-4">- Alex, Reef Keeper</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card>
            <v-card-text>
              <p>"I love the charts and how easy it is to go back and see what caused issues in my tank."</p>
              <p class="font-weight-bold mt-4">- Jamie, Nano Reef Enthusiast</p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card>
            <v-card-text>
              <p>"The QR code feature is genius. I just scan and enter my test results — done!"</p>
              <p class="font-weight-bold mt-4">- Morgan, Hobbyist</p>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container> -->

    <!-- Comparison Table Section -->
    <v-container class="py-12">
      <v-row>
        <v-col cols="12" class="text-center mb-10">
          <h2 class="text-h4 font-weight-bold">How Reeflendar Stacks Up</h2>
        </v-col>
      </v-row>
      <v-table density="compact" class="comparison-table" fixed-header>
        <thead>
          <tr>
            <th class="font-weight-bold text-body-1">Feature</th>
            <th class="font-weight-bold text-body-1">Reeflendar</th>
            <th class="font-weight-bold text-body-1">Reefability</th>
            <th class="font-weight-bold text-body-1">Apex Fusion</th>
            <th class="font-weight-bold text-body-1">Aquarimate</th>
            <th class="font-weight-bold text-body-1">ReefBeat</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Web Interface</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Apex Fusion -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- ReefBeat -->
          </tr>
          <tr>
            <td>No Hardware Needed</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reefability -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Apex Fusion -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- ReefBeat -->
          </tr>
          <tr>
            <td>Visual Charts</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Apex Fusion -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Aquarimate -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- ReefBeat -->
          </tr>
          <tr>
            <td>Free Tier</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes w/ Hardware</td>     <!-- Apex Fusion -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Aquarimate -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- ReefBeat -->
          </tr>
          <tr>
            <td>Desktop Access</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Apex Fusion -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- ReefBeat -->
          </tr>
          <tr>
            <td>Mobile Access</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Apex Fusion -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Aquarimate -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- ReefBeat -->
          </tr>
          <tr>
            <td>Photo Journal</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Apex Fusion -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- ReefBeat -->
          </tr>
          <tr>
            <td>QR Code Quick Link</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Reefability -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Apex Fusion -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- ReefBeat -->
          </tr>
          <tr>
            <td>Data Import</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="green">mdi-check</v-icon> Limited</td> <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Apex Fusion -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- ReefBeat -->
          </tr>
          <tr>
            <td>Data Export</td>
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reeflendar -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Apex Fusion -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Aquarimate -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- ReefBeat -->
          </tr>
          <tr>
            <td>Public Link Sharing</td>
            <td><v-icon color="green">mdi-check</v-icon> Soon</td>    <!-- Reeflendar -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Reefability -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Apex Fusion -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- ReefBeat -->
          </tr>
          <tr>
            <td>AI Diagnosis & Recommendations</td>
            <td><v-icon color="green">mdi-check</v-icon> Soon</td>    <!-- Reeflendar -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Reefability -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Apex Fusion -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- ReefBeat --> 
          </tr>
          <tr>
            <td>Custom User-Defined Metrics</td>
            <td><v-icon color="green">mdi-check</v-icon> Soon</td>    <!-- Reeflendar -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>        <!-- Reefability -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Apex Fusion -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- Aquarimate -->
            <td><v-icon color="green">mdi-check</v-icon> Yes</td>     <!-- ReefBeat -->
          </tr>
          <tr>
            <td>API Access</td>
            <td><v-icon color="green">mdi-check</v-icon> Soon</td>      <!-- Reeflendar -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>          <!-- Reefability -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>          <!-- Apex Fusion -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>          <!-- Aquarimate -->
            <td><v-icon color="red">mdi-close</v-icon> No</td>          <!-- ReefBeat -->
          </tr>
        </tbody>
      </v-table>
    </v-container>

    <!-- CTA Section -->
    <v-container class="py-16 text-center">
      <h2 class="text-h4 font-weight-bold mb-4">Start Logging Your Reef Today</h2>
      <p class="mb-6">Create your free account and get access to all essential features instantly.</p>
      <v-btn color="primary" @click="authStore.login" large>Start Logging Free</v-btn>
    </v-container>

    <!-- Blog Posts Section -->
    <v-container class="py-12">
      <v-row>
        <v-col cols="12" class="text-center mb-10">
          <h2 class="text-h4 font-weight-bold">Recent Blog Posts</h2>
        </v-col>
      </v-row>
      <v-row>
        <BlogPostCard
          v-for="(post, index) in recentPosts"
          :key="index"
          :thumbnail="post.thumbnail"
          :url="post.url"
          :title="post.title"
        />
      </v-row>
    </v-container>

  </div>
</template>

<script setup>
import FeatureCard from '@/components/FeatureCard.vue'
import BlogPostCard from '@/components/BlogPostCard.vue'
import { useAuthStore } from '@/store'
import { useHead } from '@vueuse/head'
import { ref, onMounted } from 'vue'

const authStore = useAuthStore()

// Animation state for hero CTA buttons
const heroBtnAnimated = ref(false)

onMounted(() => {
  window.selectedHeroImage = selectedHeroImage.split('/').pop()
})

const features = [
  {
    icon: 'mdi-thermometer',
    title: 'Multi-Parameter Tracking',
    description: 'Log temperature, pH, salinity, alkalinity, and more with precision.'
  },
  {
    icon: 'mdi-chart-line',
    title: 'Visualize Trends',
    description: 'Spot issues early with interactive charts of your water parameters.'
  },
  {
    icon: 'mdi-image',
    title: 'Photo & Note Journals',
    description: 'Attach images and notes to every log entry or milestone.'
  },
  {
    icon: 'mdi-lock',
    title: 'Google Sign-In',
    description: 'One-click secure login, no account setup required.'
  },
  {
    icon: 'mdi-share-variant',
    title: 'Public Aquarium Pages',
    description: 'Share your tank’s progress with a single link.'
  },
  {
    icon: 'mdi-cpu-64-bit',
    title: 'No Hardware Required',
    description: 'Works with any tank setup—just open your browser and log.'
  },
  {
    icon: 'mdi-qrcode-scan',
    title: 'QR Code Shortcuts',
    description: 'Scan a sticker on your tank to instantly log new data from your phone.'
  },
  {
    icon: 'mdi-database-export',
    title: 'Data Import/Export',
    description: 'Easily back up or migrate your data with CSV import/export.'
  },
  {
    icon: 'mdi-robot',
    title: 'AI-Powered Insights',
    description: 'Get smart suggestions based on your tank’s history (coming soon).'
  },/*
  {
    icon: 'mdi-api',
    title: 'API Access',
    description: 'Build custom integrations and workflows with your data (coming soon).'
  },
  {
    icon: 'mdi-cog-outline',
    title: 'Custom Metrics',
    description: 'Track anything you want with user-defined parameters (coming soon).'
  },*/
]

const recentPosts = [
  {
    thumbnail: '/images/blog/why-spreadsheets-fail-hero.jpg',
    url: '/blog/why-spreadsheets-fail-reef-keepers',
    title: 'Why Spreadsheets Fail Reef Keepers (and What to Use Instead)'
  },
  {
    thumbnail: '/images/blog/top-5-stability-hero.jpg',
    url: '/blog/top-5-ways-to-keep-your-tank-stable',
    title: 'Top 5 Ways to Keep Your Reef Tank Stable'
  },
  {
    thumbnail: '/images/blog/best-log-app-hero.jpg',
    url: '/blog/best-reef-tank-log-app-free-and-paid-options-compared',
    title: 'Best Reef Tank Log App (Free and Paid Options Compared)'
  },
  {
    thumbnail: '/images/blog/track-parameters-hero.jpg',
    url: '/blog/how-to-track-aquarium-parameters-without-expensive-controllers',
    title: 'How to Track Aquarium Parameters Without Expensive Controllers'
  },
  {
    thumbnail: '/images/blog/top-10-parameters-hero.jpg',
    url: '/blog/top-10-most-important-reef-tank-parameters-to-monitor',
    title: 'Top 10 Most Important Reef Tank Parameters to Monitor'
  },
  {
    thumbnail: '/images/blog/reef-maintenance-hero.jpg',
    url: '/blog/reef-tank-maintenance-schedule-template-free-download',
    title: 'Reef Tank Maintenance Schedule Template (Free Download)'
  },
  {
    thumbnail: '/images/blog/spreadsheets-vs-apps-hero.jpg',
    url: '/blog/spreadsheets-vs-apps-the-best-way-to-record-aquarium-data',
    title: 'Spreadsheets vs. Apps: The Best Way to Record Aquarium Data'
  },
  {
    thumbnail: '/images/blog/sharing-logs-hero.jpg',
    url: '/blog/how-sharing-your-reef-tank-logs-can-help-the-community',
    title: 'How Sharing Your Reef Tank Logs Can Help Troubleshooting'
  },
  {
    thumbnail: '/images/blog/aquarium-parameter-logging-hero.jpg',
    url: '/blog/beginners-guide-to-aquarium-parameter-logging',
    title: 'Beginner\'s Guide to Aquarium Parameter Logging'
  },
]

const heroImages = [
  new URL('@/assets/hero_blasto.jpg', import.meta.url).href,
  new URL('@/assets/hero_bta_closeup.jpg', import.meta.url).href,
  new URL('@/assets/hero_bta_pink.jpg', import.meta.url).href,
  new URL('@/assets/hero_clowns.jpg', import.meta.url).href,
  new URL('@/assets/hero_cyphastria.jpg', import.meta.url).href
]
const selectedHeroImage = heroImages[Math.floor(Math.random() * heroImages.length)]
const heroBgStyle = ref({
  backgroundImage: `url('${selectedHeroImage}')`,
  backgroundSize: 'cover', // changed from 'contain' to 'cover'
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  //boxShadow: 'inset 0 0 0 2000px rgba(255, 255, 255, 0.4)',
  backgroundColor: 'rgb(var(--v-theme-background))'
})

useHead({
  title: 'Reeflendar.com - Aquarium Management Dashboard',
  meta: [
    { name: 'description', content: 'Welcome to Reeflendar, your aquarium management dashboard for logging all your water parameters and metrics.' },
    { name: 'keywords', content: 'aquarium, reef tank, fish, water parameters, logging, fish tank, salt water, coral reef, marine life, reef management' },
  ],
})
/*
 * * * HASHTAGS * * *
#Reeflendar #reeflife #reef #reefkeeping #saltwateraquarium #coralreef #coral #aquariumlovers #reeftank #nanoreef #aquariumhobby #marineaquarium 
#reeftank #saltwateraquarium #aquarium #aquariumhobby 

YOUTUBE SHORTS HASHTAGS
#YouTubeShorts
#Shorts
#AquariumShorts

 * * * DESCRIPTIONS * * *
VERY SHORT DESCRIPTION (Under 20 Words)
Reeflendar – the free, cloud-based reef aquarium tracker. Log water parameters, visualize trends, and keep your tank thriving.

MEDIUM DESCRIPTION (Under 50 Words)
Reeflendar is a free, cloud‑based aquarium logbook for hobbyists. Track your reef tank’s vital parameters, see beautiful charts of your water chemistry, and store photos & notes – all in one place. Accessible on any device with no extra gear needed, it’s the easiest way to keep your aquarium thriving.

LONG DESCRIPTION (Under 100 Words)
Say goodbye to spreadsheets and hello to Reeflendar – your all-in-one, cloud-based reef aquarium diary. This free app lets you log every water parameter (from temperature and pH to nitrate and calcium) with ease. Interactive charts show how your reef evolves over time, helping you spot trends at a glance. Add photos and notes to create a living history of your tank. Whether you’re on your phone or at your PC, your data is always at your fingertips – no hardware required. Reeflendar is built by hobbyists, for hobbyists – making reef tank tracking fun, simple, and stunning to see.

CASUAL ELEVATOR PITCH
Keeping a reef aquarium can feel like juggling science class with art class – you’ve got to track all those water tests and enjoy the beauty of your corals. That’s where Reeflendar comes in. Think of it as your tank’s personal diary in the cloud. It’s free and super easy – log your salinity, pH, temperature, and more from any device, anytime. Then, watch the magic happen in charts that actually make your tank stats fun to review. Got a gorgeous coral growth photo or an important note about a new fish? Save it to Reeflendar and build a story of your aquarium’s journey. No complicated gizmos or subscriptions needed. Whether you’re a newbie with a single clownfish or an old-school reefer with a full-blown coral farm, Reeflendar keeps your hobby organized and exciting. It’s like having a reef buddy who remembers everything for you, so you can focus on enjoying your slice of the ocean!

SHORT DESCRIPTION FROM SPEC
Reeflendar is a web application designed for aquarium enthusiasts to track and manage their aquarium parameters, maintenance activities, and overall health.

 * * * BLOG POSTS * * *
INTRODUCTION FORUM POST
Meet Reeflendar – A Free Tool I Built to Make Aquarium Tracking Easier (and More Fun)
Hey everyone! 

My name’s Bill, and I’ve been a hobbyist reefer for a while now. Like many of you, I started with a notebook… then a spreadsheet… then a million screenshots and water test kits scattered across my desk. Eventually, I got fed up juggling all that, and I thought, “Why isn’t there a simple, visual, cloud-based way to keep track of my reef’s health?”

So I built one.

It’s called Reeflendar.com – a free web app I’ve been working on to make it easier (and kind of fun) to log and visualize your aquarium parameters, photos, and notes – all in one clean, responsive dashboard.

What It Does:
✅ Log your parameters: temp, salinity, pH, alk, calcium, nitrate, etc.
📈 View interactive charts to track trends over time
📝 Add notes and 📸 upload pictures to create a living history of your reef
🌐 Web-based so you can access it from any device – no hardware or downloads needed
📱 Stick the QR code sticker somewhere discrete around your tank, when you need to access your aquarium quickly just scan with your phone
🔄 Import and export data freely from any spreadsheet or other system
🔗 Coming soon: Share a read-only link to your log with friends, mentors, or forums for helpful advice or troubleshooting
🧠 Coming soon: AI-powered suggestions based on your trends

It is cloud based and works on any device (phone, tablet, desktop) and you don’t need to buy any hardware or download anything. Just head to Reeflendar.com, log in with Google, and start adding your tanks.

Why I’m Sharing This Here
Reeflendar is still new, but it’s live and fully functional. I built it to solve a real pain point I kept running into as a hobbyist – and now I want to put it in the hands of other reef keepers to see if it can help them too.

And that’s where you come in!

I’d love your feedback – what works, what’s missing, what would make it a must-use tool for your reefing routine. Whether you’re just dipping a toe into saltwater or you’re running a full-blown coral farm, your thoughts could help shape the next round of features.

Help Me Make This Better
So if you’ve got a few minutes, please check out https://Reeflendar.com, create an aquarium, and poke around. And if you run into anything weird, or think “hey it would be cool if it did X,” just shoot me a message or drop a comment here.

No strings attached. It’s totally free to use, and always will be for core features.

Thanks for reading, and happy reefing!

* * * FEATURES HIGHLIGHT * * *
✅ 100% Free & Hobbyist-Friendly – No subscriptions, no paywalls. Just a tool made by and for reef lovers.
🌐 Cloud-Based, Cross-Platform Access – Works on any device (desktop, tablet, phone). No hardware required.
📊 Track Vital Parameters – Easily log temp, salinity, pH, ammonia, nitrate, calcium, alkalinity, and more—all in one place.
📈 Beautiful Charts – Interactive graphs help you visualize trends and spot issues before they affect your tank.
📝 Photo & Note Logging – Document coral growth, maintenance routines, or fish behavior with timestamped notes and images.
🔗 Shareable Tank Logs – Generate secure, read-only links to share your logs with friends, mentors, or forums for advice.
📎 Printable QR Codes – Quickly jump to any aquarium from your phone by scanning a custom QR code—perfect for labeling tanks or testing stations.
🔄 Data Import/Export – Download your full log or bring in data from spreadsheets and other systems.
⚙️ No Extra Gear Needed – Unlike some other systems, Reeflendar doesn’t require a controller or any additional equipment.
🚀 Coming Soon: AI-Powered Insights – Get smart suggestions and alerts based on your tank’s history.
🧪 Coming Soon: API Access for Power Users – Build your own workflows or integrations using Reeflendar’s data.
📐 Coming Soon: Custom Metrics – Track anything you want beyond the standard parameters—fully user-defined.

*/
</script>

<style scoped>
.hero-section {
  background-size: cover;
  background-position: center;
  min-height: 600px; /* add or increase min-height for more vertical space */
  padding-top: 50px;
}

.hero-logo {
  max-width: 400px;
  width: 100%;
  height: auto;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.hero-btn-group {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 32px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

/* Add hover grow effect for CTA buttons */
.hero-btn-group .v-btn {
  transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1);
}
.hero-btn-group .v-btn:hover {
  transform: scale(1.12);
}
</style>
